import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowUp } from 'lucide-react';

/**
 * Prompt-first composer for the signed-out /write landing.
 *
 * The composer is the page: a lit surface with a slow sheen on its border, a
 * placeholder that keeps offering real jobs while the field is empty, and a
 * submit that is never visually dead. Chips are fill-only: they never send.
 * All intent storage stays in the parent — this component only reports a
 * submitted prompt.
 */

export const STARTER_CHIPS = [
  'Draft a related-work section',
  'Turn my outline into a paper',
  'Add citations to my introduction',
  'Tighten my abstract to 150 words',
];

const ROTATING_PLACEHOLDERS = [
  'Describe your paper, or paste an abstract or outline…',
  'A survey of agentic AI in telecommunications…',
  'Turn these bullet points into a methods section…',
  'Add citations to my introduction and build the bibliography…',
];

const ROTATE_MS = 3500;

interface PromptComposerProps {
  onSubmitPrompt: (prompt: string) => void;
  /** Starter chips are an onboarding device — off on the signed-in home. */
  showChips?: boolean;
  /** Label of the primary action. */
  submitLabel?: string;
  /** Resting hint shown while the field is empty. */
  idleHint?: string | null;
  /** Disables the field and the submit while a draft is being created. */
  busy?: boolean;
  /** Reports which starter chip is hovered/focused (null when none). */
  onChipIntent?: (chip: string | null) => void;
  /** Reports whether the visitor is engaged (focused or has text). */
  onActiveChange?: (active: boolean) => void;
  /** Desktop focus is useful on /write, but deliberately disabled on broader landing pages. */
  autoFocus?: boolean;
}


const isCoarsePointer = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

const PromptComposer: React.FC<PromptComposerProps> = ({
  onSubmitPrompt,
  showChips = true,
  submitLabel = 'Start writing',
  idleHint = 'LaTeX, citations and compiling are handled for you',
  busy = false,
  onChipIntent,
  onActiveChange,
  autoFocus = true,
}) => {
  const [prompt, setPrompt] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [nudge, setNudge] = useState(false);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const prefersReducedMotion = useReducedMotion();

  const dirty = prompt.length > 0;

  // The parent drives the proof animation off engagement.
  useEffect(() => {
    onActiveChange?.(focused || dirty);
  }, [focused, dirty, onActiveChange]);

  // Desktop only: a caret already blinking in the field is the strongest
  // possible invitation. Never on touch — it would throw up the keyboard.
  useEffect(() => {
    if (!autoFocus || isCoarsePointer()) return;
    inputRef.current?.focus({ preventScroll: true });
  }, [autoFocus]);

  // The field keeps offering jobs while it is empty.
  useEffect(() => {
    if (prefersReducedMotion || dirty) return;
    const id = setInterval(
      () => setPlaceholderIndex((i) => (i + 1) % ROTATING_PLACEHOLDERS.length),
      ROTATE_MS,
    );
    return () => clearInterval(id);
  }, [prefersReducedMotion, dirty]);

  const fillFromChip = (chip: string) => {
    setPrompt(chip);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    });
  };

  const submit = () => {
    const trimmed = prompt.trim();
    if (!trimmed) {
      // Never a silent no-op: point at the field and pulse it.
      inputRef.current?.focus();
      setNudge(true);
      window.setTimeout(() => setNudge(false), 600);
      return;
    }
    onSubmitPrompt(trimmed);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submit();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const placeholder = prefersReducedMotion
    ? ROTATING_PLACEHOLDERS[0]
    : ROTATING_PLACEHOLDERS[placeholderIndex];

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <motion.div
        animate={nudge && !prefersReducedMotion ? { x: [0, -5, 5, -3, 0] } : { x: 0 }}
        transition={{ duration: 0.35 }}
        className="relative"
      >
        {/* Travelling sheen, clipped to a hairline ring around the card */}
        <div
          aria-hidden="true"
          className="writer-composer-sheen pointer-events-none absolute -inset-px overflow-hidden rounded-[28px] opacity-60"
        />

        <div className="relative rounded-[28px] border border-border/60 bg-card shadow-[0_24px_60px_-24px_hsl(var(--foreground)/0.25)] transition duration-300 focus-within:border-primary/60 focus-within:shadow-[0_28px_70px_-24px_hsl(var(--primary)/0.45)] focus-within:ring-2 focus-within:ring-primary/20">
          <div className="relative">
            {!dirty && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-6 top-6 text-base text-muted-foreground/70 sm:text-lg"
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.span
                    key={placeholder}
                    initial={prefersReducedMotion ? false : { opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={prefersReducedMotion ? undefined : { opacity: 0, y: -6 }}
                    transition={{ duration: 0.35, ease: 'easeOut' }}
                    className="block"
                  >
                    {placeholder}
                  </motion.span>
                </AnimatePresence>
              </div>
            )}

            <textarea
              ref={inputRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              rows={3}
              disabled={busy}
              className="relative w-full resize-none bg-transparent px-6 pt-6 pb-2 text-base outline-none disabled:opacity-60 sm:text-lg"
              aria-label="Describe your paper"
            />
          </div>

          <div className="flex flex-col gap-3 px-6 pb-5 sm:flex-row sm:items-center sm:justify-between">
            {(dirty || idleHint) && (
              <span className="text-xs text-muted-foreground/80">
                {dirty ? 'Enter to start · Shift+Enter for a new line' : idleHint}
              </span>
            )}
            <motion.button
              type="submit"
              disabled={busy}
              whileTap={prefersReducedMotion || busy ? undefined : { scale: 0.97 }}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow-lg shadow-primary/25 transition hover:bg-primary/90 disabled:opacity-70 sm:w-auto"
            >
              {busy ? 'Setting up…' : submitLabel}
              <ArrowUp className="h-4 w-4 transition-transform group-hover:-translate-y-0.5" />
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* One scrollable row: "there is more" invites a swipe on mobile */}
      {showChips && (
        <div className="-mx-6 mt-4 overflow-x-auto px-6 [-ms-overflow-style:none] [scrollbar-width:none] sm:mx-0 sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden">
          <div className="flex w-max gap-2 sm:w-full sm:flex-wrap sm:justify-center">
            {STARTER_CHIPS.map((chip) => (
              <motion.button
                key={chip}
                type="button"
                onClick={() => fillFromChip(chip)}
                onMouseEnter={() => onChipIntent?.(chip)}
                onMouseLeave={() => onChipIntent?.(null)}
                onFocus={() => onChipIntent?.(chip)}
                onBlur={() => onChipIntent?.(null)}
                whileTap={prefersReducedMotion ? undefined : { scale: 0.96 }}
                className="whitespace-nowrap rounded-full border border-border/60 bg-card/80 px-4 py-2 text-sm text-foreground/80 backdrop-blur transition hover:border-primary/50 hover:text-foreground"
              >
                {chip}
              </motion.button>
            ))}
          </div>
        </div>
      )}

    </form>
  );
};

export default PromptComposer;
